/**
 * CreatePostModal — modal for composing new pplTalk posts.
 * Auth-gated: anonymous users are redirected to login before the modal opens.
 *
 * Features:
 *  - Title + optional body
 *  - Up to 3 category tags from the standard 12 categories
 *  - Content moderation handled server-side by talkValidatorEurW
 *  - Loading/error states
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { PPLTALK_CATEGORIES } from '../types/pplTalk';
import type { PplTalkCategory } from '../types/pplTalk';
import { createPost } from '../services/pplTalkService';
import { useAuth } from '../hooks/useAuth';

interface CreatePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPostCreated: () => void;
}

const MAX_CATEGORIES = 4;
const MAX_TITLE_LENGTH = 150;
const MAX_BODY_LENGTH = 500;

const CreatePostModal: React.FC<CreatePostModalProps> = ({ isOpen, onClose, onPostCreated }) => {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<PplTalkCategory[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Focus title input when modal opens
  useEffect(() => {
    if (isOpen && titleInputRef.current) {
      setTimeout(() => titleInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !submitting) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, submitting, onClose]);

  // Focus trap — cycle Tab within modal
  useEffect(() => {
    if (!isOpen) return;
    const modal = modalRef.current?.querySelector('.create-post-modal') as HTMLElement | null;
    if (!modal) return;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = modal.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  // Close on backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === modalRef.current && !submitting) {
        onClose();
      }
    },
    [submitting, onClose]
  );

  const toggleCategory = useCallback((category: PplTalkCategory) => {
    setSelectedCategories((prev) => {
      if (prev.includes(category)) {
        return prev.filter((c) => c !== category);
      }
      if (prev.length >= MAX_CATEGORIES) return prev;
      return [...prev, category];
    });
  }, []);

  const resetForm = useCallback(() => {
    setTitle('');
    setBody('');
    setSelectedCategories([]);
    setError(null);
    setSuccess(false);
  }, []);

  const handleClose = useCallback(() => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  }, [submitting, resetForm, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!title.trim()) {
      setError('Please enter a title for your post.');
      return;
    }
    if (!user) return;

    setSubmitting(true);
    setError(null);

    try {
      const userName =
        (user as Record<string, unknown>).tetherHandle as string ||
        (user as Record<string, unknown>).firstName as string ||
        user.displayName ||
        'Anonymous';

      const result = await createPost({
        title: title.trim(),
        body: body.trim() || undefined,
        categories: selectedCategories,
        userName,
        userAvatar: (user as Record<string, unknown>).avatar as string || null,
      });

      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          resetForm();
          onClose();
          onPostCreated();
        }, 1200);
      } else {
        setError(result.reason || 'Your post was not approved. Please review the content and try again.');
      }
    } catch (err: unknown) {
      console.error('Error creating post:', err);
      const firebaseError = err as { code?: string; message?: string };
      if (firebaseError.code === 'functions/unauthenticated') {
        setError('You must be logged in to create a post.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [title, body, selectedCategories, user, resetForm, onClose, onPostCreated]);

  if (!isOpen) return null;

  return (
    <div className="create-post-backdrop" ref={modalRef} onClick={handleBackdropClick}>
      <div className="create-post-modal" role="dialog" aria-modal="true" aria-label="Create a post">
        {/* Header */}
        <div className="create-post-header">
          <h2>create a post</h2>
          <button
            className="create-post-close"
            onClick={handleClose}
            disabled={submitting}
            type="button"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Success state */}
        {success && (
          <div className="create-post-success">
            <span className="create-post-success-icon">✓</span>
            <p>your post is live!</p>
          </div>
        )}

        {/* Form */}
        {!success && (
          <div className="create-post-body">
            {/* Title */}
            <div className="create-post-field">
              <input
                ref={titleInputRef}
                type="text"
                className="create-post-title-input"
                placeholder="what's on your mind?"
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, MAX_TITLE_LENGTH))}
                disabled={submitting}
                maxLength={MAX_TITLE_LENGTH}
              />
              <span className="create-post-char-count">
                {title.length}/{MAX_TITLE_LENGTH}
              </span>
            </div>

            {/* Body */}
            <div className="create-post-field">
              <textarea
                className="create-post-body-input"
                placeholder="share more details... (optional)"
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY_LENGTH))}
                disabled={submitting}
                rows={4}
                maxLength={MAX_BODY_LENGTH}
              />
              <span className="create-post-char-count">
                {body.length}/{MAX_BODY_LENGTH}
              </span>
            </div>

            {/* Categories */}
            <div className="create-post-categories">
              <label className="create-post-label">
                topics (up to {MAX_CATEGORIES})
              </label>
              <div className="create-post-category-chips">
                {PPLTALK_CATEGORIES.map((category) => (
                  <button
                    key={category}
                    type="button"
                    className={`filter-chip ${selectedCategories.includes(category) ? 'active' : ''}`}
                    onClick={() => toggleCategory(category)}
                    disabled={submitting || (!selectedCategories.includes(category) && selectedCategories.length >= MAX_CATEGORIES)}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="create-post-error">
                <p>{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="create-post-actions">
              <button
                className="btn btn-ghost"
                onClick={handleClose}
                disabled={submitting}
                type="button"
              >
                cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={submitting || !title.trim()}
                type="button"
              >
                {submitting ? 'posting...' : 'post'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatePostModal;
